import { useRef, useState, useEffect, useCallback, FC } from 'react';
import { connect } from 'react-redux';
import * as tracking from 'dibs-tracking';
import { trackChatOpen } from './helpers/tracking';
import { SESSION_CHAT_STATE_KEY } from './constants';
import { localStorage } from 'dibs-browser-storage';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { getAbTestTrackingData } from 'dibs-buyer-layout/exports/clientABTestHelpers';

// components
import { BuyerChatRespLazy } from 'dibs-buyer-chat/exports/BuyerChatRespLazy';
import { BuyerChatRespVariantLazy } from 'dibs-buyer-chat/exports/BuyerChatRespVariantLazy';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

// types
import { PdpRespDealerChatLoader_viewer as Viewer } from './__generated__/PdpRespDealerChatLoader_viewer.graphql';

type Props = {
    viewer: Viewer;
    isInteractivePdpChatVariant: boolean;
    isInteractivePdpChat: boolean;
};

export const PdpRespDealerChatLoaderComponent: FC<Props> = ({
    viewer,
    isInteractivePdpChat,
    isInteractivePdpChatVariant,
}) => {
    const chatVisibilityRef = useRef(null);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isChatVisible, setIsChatVisible] = useState(false);

    useEffect(() => {
        if (isChatVisible) {
            const { isMinimized: isInitiallyMinimized = false } =
                (localStorage.getItem(SESSION_CHAT_STATE_KEY) as { isMinimized: boolean }) || {};
            trackChatOpen(isInitiallyMinimized);
        }
    }, [isChatVisible]);

    useEffect(() => {
        const { isMinimized: isInitiallyMinimized = false } =
            (localStorage.getItem(SESSION_CHAT_STATE_KEY) as { isMinimized: boolean }) || {};
        setIsMinimized(isInitiallyMinimized);

        // AB Test tracking
        const trackingData = getAbTestTrackingData(document.cookie, 'PDP_CHAT_V3');
        if (trackingData?.testGroup) {
            tracking.trackAbTestEvent(trackingData, 'pdp_pageview');
        }
    }, []);

    const handleVisibilityChange = useCallback(({ isVisible, boundingClientRect, disconnect }) => {
        if (isVisible || boundingClientRect?.top < 0) {
            setIsChatVisible(true);
            disconnect();
        }
    }, []);

    return (
        <>
            <div ref={chatVisibilityRef} />
            <VisibilityTracker
                elementRef={chatVisibilityRef}
                onVisibilityChange={handleVisibilityChange}
            />
            {isChatVisible &&
                (isInteractivePdpChat ? (
                    <BuyerChatRespLazy viewer={viewer} isInitiallyMinimized={isMinimized} />
                ) : (
                    isInteractivePdpChatVariant && (
                        <BuyerChatRespVariantLazy
                            viewer={viewer}
                            isInitiallyMinimized={isMinimized}
                        />
                    )
                ))}
        </>
    );
};

type ReduxStoreType = {
    relayVariables: {
        variables: {
            isInteractivePdpChatVariant: boolean;
            isInteractivePdpChat: boolean;
        };
    };
};

function mapStateToProps(
    state: ReduxStoreType
): { isInteractivePdpChat: boolean; isInteractivePdpChatVariant: boolean } {
    const {
        relayVariables: { variables },
    } = state;

    return {
        isInteractivePdpChat: !!variables?.isInteractivePdpChat,
        isInteractivePdpChatVariant: !!variables?.isInteractivePdpChatVariant,
    };
}

export const PdpRespDealerChatLoader = createFragmentContainer(
    connect(mapStateToProps)(PdpRespDealerChatLoaderComponent),
    {
        viewer: graphql`
            fragment PdpRespDealerChatLoader_viewer on Viewer {
                ...BuyerChatRespLazy_viewer @include(if: $isInteractivePdpChat)
                ...BuyerChatRespVariantLazy_viewer @include(if: $isInteractivePdpChatVariant)
            }
        `,
    }
);
